import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { Text } from 'fable/flowEditor/components/flowManager/canvas/node/text';
import { TextContent } from 'fable/flowEditor/components/flowManager/canvas/node/textContent';
import { TextEllipsis } from 'fable/flowEditor/components/flowManager/canvas/node/textEllipsis';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { MessengerExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';

import { FlowMessengerNodeCustomFormPayload } from '../../../flow.types';
import { MessengerCustomFormNodeModal } from '../components/customFormsModal';

const translatePayload: MessengerCustomFormNodeAttributes['translatePayload'] =
  ({ payload }) => {
    const isContactForm = 'contactForm' in payload;

    if (isContactForm) {
      // A contact form is a terminal node, so it doesn't have any connections
      return [];
    }

    return [
      {
        id: uuid(),
        label: 'arrow',
        connection: payload.nextNodeId || uuid(),
      },
    ];
  };

const updateConnection: MessengerCustomFormNodeAttributes['updateConnection'] =
  (node, _connection, connectionId) => {
    return {
      ...node,
      payload: {
        ...node.payload,
        nextNodeId: connectionId,
      },
    };
  };

type MessengerCustomFormNodeAttributes =
  GenericNodeAttributes<FlowMessengerNodeCustomFormPayload>;

export const MessengerCustomFormNodeAttributes: MessengerCustomFormNodeAttributes =
  {
    iconName: 'list-ul',
    label: 'Form',
    color: hexStringToNumber(flowNodeColor.orange),
    translatePayload,
    updateConnection,
    NodeContent: MessengerCustomFormNode,
    ActionModal: MessengerCustomFormNodeModal,
  };

type MessengerCustomFormNodeProps = GenericNodeProps<
  FlowMessengerNodeCustomFormPayload,
  MessengerExtraData
>;

export function MessengerCustomFormNode({
  node,
  context: { extraData },
}: MessengerCustomFormNodeProps): JSX.Element {
  const { payload } = node;
  const { queues, forms = [] } = extraData;

  const isContactForm = 'contactForm' in payload;

  if (isContactForm) {
    const { queueId } = payload.contactForm;
    const queueName =
      queues.find((queue) => queue.id === queueId)?.name ?? 'Unknown queue';

    return (
      <TextContent maxLines={2}>
        <TextEllipsis>
          <Text weight="normal" text="Queue" />
          <Text weight="semibold" text={queueName} />
        </TextEllipsis>
      </TextContent>
    );
  }

  const formTitle =
    forms.find((form) => form.id === payload.formId)?.title ?? 'Unknown form';

  return (
    <TextContent maxLines={2}>
      <TextEllipsis>
        <Text weight="semibold" text={formTitle} />
      </TextEllipsis>
    </TextContent>
  );
}
